import React, {useEffect, useState} from "react";
import {Badge, Button} from "reactstrap";
import {DEFAULT_LOOK, LOOKS, chooseLook, currentLook, onLookChanged} from "../utils/look";
import IconLabel from "./IconLabel";
import {RefreshIcon} from "./icons";
import "./LookPicker.css";

/**
 * "Estilo": the app's base look, one option per style. Choosing one applies it
 * right away and remembers it on this device. When `save` is given it is also
 * asked to keep the choice (it returns a promise); if that fails the previous
 * style comes back and `onSaveError` is called. All but the default are
 * marked "Beta", and there is always a way back to the default.
 *
 * @param {{
 *   save?: (id: string) => Promise<unknown>,
 *   onSaveError?: () => void,
 *   disabled?: boolean,
 * }} props
 */
const LookPicker = ({save, onSaveError, disabled = false}) => {
    const [current, setCurrent] = useState(currentLook);

    // The saved look arrives from the server after the page is up.
    useEffect(() => onLookChanged(() => setCurrent(currentLook())), []);

    const choose = async (id) => {
        const previous = current;
        chooseLook(id);
        if (!save) return;
        try {
            await save(id);
        } catch {
            chooseLook(previous);
            onSaveError?.();
        }
    };

    return (
        <div>
            <div className="d-flex flex-wrap gap-2" role="group" aria-label="Estilo do app">
                {LOOKS.map(({id, label, description, beta}) => (
                    <button
                        key={id}
                        type="button"
                        className="look-option"
                        aria-pressed={current === id}
                        disabled={disabled}
                        onClick={() => choose(id)}
                    >
                        <span className="d-flex align-items-center gap-2">
                            <span className="look-option__name">{label}</span>
                            {beta && (
                                <Badge color="warning" className="text-dark">
                                    Beta
                                </Badge>
                            )}
                        </span>
                        <span className="small text-muted">{description}</span>
                    </button>
                ))}
            </div>
            <div className="small text-muted mt-2">
                Estilos em Beta são experimentais: algumas telas podem ficar desalinhadas.
                {current !== DEFAULT_LOOK && (
                    <Button
                        color="link"
                        size="sm"
                        className="p-0 ms-2 align-baseline"
                        disabled={disabled}
                        onClick={() => choose(DEFAULT_LOOK)}
                    >
                        <IconLabel icon={<RefreshIcon />}>Voltar ao estilo padrão</IconLabel>
                    </Button>
                )}
            </div>
        </div>
    );
};

export default LookPicker;
